import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import displayError from '../../utils/ErrorHandler';
import authenticate from '../../utils/Authenticate';

const STATUS_STYLES = {
  pending:          'bg-yellow-100 text-yellow-800 border-yellow-300',
  received:         'bg-green-100 text-green-800 border-green-300',
  'Items Received': 'bg-green-100 text-green-800 border-green-300',
  dispatched:       'bg-blue-100 text-blue-800 border-blue-300',
};

const statusStyle = (s) => STATUS_STYLES[s] || 'bg-gray-100 text-gray-700 border-gray-300';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const isOverdue = (d, status) => {
  if (!d || status === 'received' || status === 'Items Received') return false;
  return new Date(d) < new Date();
};

const LogisticsHome = () => {
  const [pos, setPOs] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [collapsedZones, setCollapsedZones] = useState({});
  const { user, setUser } = useContext(UserContext);
  const navigate = useNavigate();

  useEffect(() => {
    async function startup() {
      if (!user) await authenticate(setUser, navigate);
    }
    startup();
  }, [user, navigate, setUser]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URI}/api/po/getAll`, { withCredentials: true })
      .then((res) => {
        setPOs(res.data.data || []);
        setLoading(false);
      })
      .catch((err) => {
        const errorMessage = displayError(err, navigate, setUser);
        if (errorMessage) setMessage(errorMessage);
        setLoading(false);
      });
  }, []);

  const toggleZone = (zoneName) =>
    setCollapsedZones((prev) => ({ ...prev, [zoneName]: !prev[zoneName] }));

  // Filter across zone name, supplier name, incharge
  const q = searchQuery.toLowerCase();
  const filtered = pos.filter((po) => {
    if (!q) return true;
    return (
      po.zone?.desc?.toLowerCase().includes(q) ||
      po.supplier?.name?.toLowerCase().includes(q) ||
      po.poIncharge?.toLowerCase().includes(q)
    );
  });

  // Group by zone
  const zoneMap = {};
  filtered.forEach((po) => {
    const key = po.zone?.desc || 'Unknown Zone';
    if (!zoneMap[key]) zoneMap[key] = { zoneId: po.zone?.id ?? 999, pos: [] };
    zoneMap[key].pos.push(po);
  });

  // Sort zones by numeric zone id
  const sortedZones = Object.entries(zoneMap).sort((a, b) => a[1].zoneId - b[1].zoneId);

  // Summary stats
  const total = pos.length;
  const pending = pos.filter((p) => p.status === 'pending').length;
  const received = pos.filter((p) => ['received', 'Items Received'].includes(p.status)).length;
  const overdue = pos.filter((p) => isOverdue(p.deliveryDate, p.status)).length;
  const uniqueZones = new Set(pos.map((p) => p.zone?.desc)).size;

  return (
    <div className="p-4 max-w-5xl mx-auto">
      {message && (
        <div className="bg-red-100 border border-red-300 text-red-500 px-4 py-3 rounded mb-4">
          {message}
        </div>
      )}

      {/* Summary strip */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: 'Zones', value: uniqueZones, color: 'bg-indigo-50 text-indigo-800' },
            { label: 'Total POs', value: total, color: 'bg-gray-50 text-gray-800' },
            { label: 'Pending', value: pending, color: 'bg-yellow-50 text-yellow-800' },
            { label: overdue > 0 ? `Overdue` : 'Received', value: overdue > 0 ? overdue : received, color: overdue > 0 ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800' },
          ].map(({ label, value, color }) => (
            <div key={label} className={`rounded-xl p-3 text-center shadow-sm border ${color}`}>
              <div className="text-2xl font-bold">{value}</div>
              <div className="text-xs font-medium mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      {!loading && (
        <input
          type="text"
          className="w-full mb-5 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fmb text-sm"
          placeholder="Search by zone, supplier or incharge..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      )}

      {loading && <p className="text-gray-500 text-sm">Loading purchase orders...</p>}
      {!loading && sortedZones.length === 0 && (
        <p className="text-gray-500 text-sm">No purchase orders found.</p>
      )}

      {/* Zone sections */}
      {sortedZones.map(([zoneName, { pos: zonePOs }]) => {
        const isCollapsed = collapsedZones[zoneName];
        const zoneReceived = zonePOs.filter((p) => ['received','Items Received'].includes(p.status)).length;
        const zoneOverdue = zonePOs.filter((p) => isOverdue(p.deliveryDate, p.status)).length;

        return (
          <div key={zoneName} className="mb-6">
            {/* Zone header */}
            <button
              onClick={() => toggleZone(zoneName)}
              className="w-full flex items-center justify-between bg-fmb text-white px-4 py-2.5 rounded-xl shadow-sm mb-2"
            >
              <div className="flex items-center gap-3">
                <span className="font-semibold text-base">{zoneName}</span>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">
                  {zonePOs.length} PO{zonePOs.length !== 1 ? 's' : ''}
                </span>
                {zoneOverdue > 0 && (
                  <span className="text-xs bg-red-500 px-2 py-0.5 rounded-full">
                    {zoneOverdue} overdue
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span>{zoneReceived}/{zonePOs.length} received</span>
                <span className="text-lg">{isCollapsed ? '›' : '⌄'}</span>
              </div>
            </button>

            {/* PO cards */}
            {!isCollapsed && (
              <div className="space-y-3">
                {zonePOs
                  .slice()
                  .sort((a, b) => new Date(a.deliveryDate || 0) - new Date(b.deliveryDate || 0))
                  .map((po) => {
                    const overduePO = isOverdue(po.deliveryDate, po.status);
                    return (
                      <div
                        key={po._id}
                        onClick={() => po.masterPO && navigate(`/central/masterPo/${po.masterPO}`)}
                        className={`bg-white rounded-xl border shadow-sm p-4 cursor-pointer hover:shadow-md transition-shadow ${overduePO ? 'border-red-300' : 'border-gray-200'}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          {/* Left: supplier + incharge */}
                          <div>
                            <div className="font-semibold text-gray-900 text-sm">
                              {po.supplier?.name || 'Unknown Supplier'}
                            </div>
                            {po.supplier?.company && (
                              <div className="text-xs text-gray-500">{po.supplier.company}</div>
                            )}
                            {po.poIncharge && (
                              <div className="text-xs text-gray-500 mt-0.5">
                                Incharge: <span className="font-medium text-gray-700">{po.poIncharge}</span>
                              </div>
                            )}
                          </div>

                          {/* Right: status badge */}
                          <span className={`text-xs font-semibold px-3 py-1 rounded-full border ${statusStyle(po.status)}`}>
                            {po.status || 'Pending'}
                          </span>
                        </div>

                        {/* Delivery date + item count row */}
                        <div className="mt-3 flex flex-wrap gap-3 text-xs text-gray-600">
                          <div className={`flex items-center gap-1 ${overduePO ? 'text-red-600 font-semibold' : ''}`}>
                            <span>📅</span>
                            <span>Delivery: {fmtDate(po.deliveryDate)}</span>
                            {overduePO && <span className="ml-1 text-red-500">(Overdue)</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            <span>📦</span>
                            <span>{po.items?.length || 0} item type{po.items?.length !== 1 ? 's' : ''}</span>
                          </div>
                          {po.grn && (
                            <div className="flex items-center gap-1">
                              <span>🧾</span>
                              <span>GRN: {po.grn}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1 text-gray-400">
                            <span>🗓</span>
                            <span>Created: {fmtDate(po.createdAt)}</span>
                          </div>
                        </div>

                        {po.remarks && (
                          <div className="mt-2 text-xs text-gray-500 italic border-t pt-2">
                            {po.remarks}
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LogisticsHome;
